import { useCallback } from "react";

import {
    type ModelValidationResult,
    type ResponseStatus,
    type SaveMode,
    type IModelValidationResponse,
    type IReturn
} from "src/api/Tmd.dtos";
import useServiceStackClient from "src/hooks/api/useServiceStackClient";

/**
 * Defines the response payload and status expected by this shared request adapter.
 */
export type TMDFormSaveResponse<TModel> = {
    responseStatus: ResponseStatus;
    model: TModel;
    validation: ModelValidationResult;
};

/**
 * Defines the typed request payload constructed by this shared adapter.
 */
export type TMDFormSaveRequest<
    TModel,
    TResponse extends TMDFormSaveResponse<TModel>
> = IReturn<TResponse> & {
    model: TModel;
    mode: SaveMode;
};

/**
 * Returns the model-and-save-mode POST callback for a form request type.
 *
 * Postconditions: Callback fulfillment returns the raw server response; construction and client
 * failures propagate. No local validation interpretation or cache refresh occurs here.
 *
 * Assumptions: The constructor remains stable because only the client participates in callback
 * dependencies.
 */
const useBaseFormSubmit = <
    TModel,
    TResponse extends TMDFormSaveResponse<TModel>,
    TRequest extends TMDFormSaveRequest<TModel, TResponse>
>(
    constructor: new (init?: Partial<TRequest>) => TRequest
): ((model: TModel, mode: SaveMode) => Promise<TResponse>) => {
    const client = useServiceStackClient();

    return useCallback(
        async (model: TModel, mode: SaveMode) => {
            const response = await client.post(
                new constructor({
                    model,
                    mode
                } as Partial<TRequest>)
            );

            return response;
        },
        [client]
    );
};

export default useBaseFormSubmit;
